import * as React from "react";

import type { BillingClientIdentity } from "api/billing";
import { getApiErrorMessage } from "api/apiError";
import { isConnected, TenantIdentity } from "Components/Tenants";
import { Button, ErrorState, Skeleton } from "Components/ui";
import { Download, RefreshCw } from "Components/ui/icons";
import { cn } from "utils/cn";

// Pieces shared by the billing report drawers (Collections, Outstanding).

export interface SummaryFigure {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: "default" | "danger";
}

/** The report's headline figures in one band; the first is the one the tile showed. */
export const ReportSummary: React.FC<{ figures: SummaryFigure[]; loading?: boolean }> = ({
  figures,
  loading = false,
}) => (
  <dl
    aria-busy={loading || undefined}
    className="grid divide-y divide-border overflow-hidden rounded-card border border-border bg-surface-muted/50 sm:grid-cols-3 sm:divide-x sm:divide-y-0"
  >
    {figures.map((figure, index) => (
      <div key={figure.label} className="min-w-0 px-4 py-3">
        <dt className="text-xs font-medium text-muted-foreground">{figure.label}</dt>
        <dd
          className={cn(
            "mt-1 truncate font-semibold tabular-nums tracking-tight",
            index === 0 ? "text-2xl" : "text-xl",
            figure.tone === "danger" ? "text-danger-text" : "text-foreground"
          )}
        >
          {loading ? <Skeleton className="h-7 w-24" /> : figure.value}
        </dd>
        {figure.hint && !loading && (
          <dd className="mt-0.5 truncate text-xs text-muted-foreground">{figure.hint}</dd>
        )}
      </div>
    ))}
  </dl>
);

/** A client in a report row, greyed out when its TeleOpd isn't connected. */
export const ReportClient: React.FC<{ client: BillingClientIdentity }> = ({ client }) => (
  <TenantIdentity
    name={client.tenantName}
    slug={client.slug}
    uri={client.uri}
    muted={!isConnected(client.link)}
    className="py-1.5"
  />
);

/** Heading above a report's table, with the CSV export on the right. */
export const ReportTableHeading: React.FC<{
  title: string;
  detail?: React.ReactNode;
  onExport?: () => void;
  children?: React.ReactNode;
}> = ({ title, detail, onExport, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-3">
    <div className="min-w-0">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {detail && <p className="text-xs text-muted-foreground">{detail}</p>}
    </div>
    <div className="flex flex-wrap items-center gap-3">
      {children}
      {onExport && (
        <Button size="sm" variant="secondary" leftIcon={<Download />} onClick={onExport}>
          Export CSV
        </Button>
      )}
    </div>
  </div>
);

export const ReportError: React.FC<{ error: unknown; onRetry: () => void }> = ({
  error,
  onRetry,
}) => (
  <ErrorState
    title="This report didn't load"
    description={getApiErrorMessage(error)}
    action={
      <Button variant="secondary" leftIcon={<RefreshCw />} onClick={onRetry}>
        Try again
      </Button>
    }
  />
);

/**
 * Opens a report drawer with focus on the drawer itself rather than its first control, so no
 * focus ring lands on the period picker and the first Escape closes the drawer.
 */
export const focusDrawer = (event: Event) => {
  event.preventDefault();
  (event.currentTarget as HTMLElement | null)?.focus();
};

/** Paise → rupees for CSV cells (a plain number spreadsheets can sum). */
export const csvRupees = (paise: number): number => Math.round(paise) / 100;

/** The client columns every report CSV starts with. */
export const CLIENT_CSV_COLUMNS = [
  { header: "Client", value: (row: BillingClientIdentity) => row.tenantName },
  { header: "pm2 app", value: (row: BillingClientIdentity) => row.slug },
  { header: "URL", value: (row: BillingClientIdentity) => row.uri },
  {
    header: "TeleOpd",
    value: (row: BillingClientIdentity) => (isConnected(row.link) ? "Connected" : "Not connected"),
  },
];
